/** @jsxImportSource @emotion/react */
import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { Interpolation, Theme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import Colors from "styles/colors";
import { useReportTags } from "pages/reports/bi-dashboard-next/hooks/useReportTags";
import Chip from "components/atoms/Chip.atom";

interface AddTagDialogProps {
  reportId: number;
  onClose: () => void;
  css?: Interpolation<Theme>;
  isOpen: boolean;
}

const AddTagDialog: React.FC<AddTagDialogProps> = ({
  reportId,
  onClose,
  css: cssProp,
  isOpen,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { createMultipleTags, isCreatingTag, createMultipleTagsError } =
    useReportTags();
  const { t } = useTranslation("reports");

  useEffect(() => {
    if (createMultipleTagsError) {
      setShowError(true);
      if (
        createMultipleTagsError.message?.includes("length") ||
        createMultipleTagsError.message?.includes("character")
      ) {
        setErrorMessage(
          t("Tag creation failed. Tags must be less than 200 characters."),
        );
      } else {
        setErrorMessage(t("Failed to create tags. Please try again."));
      }
    }
  }, [createMultipleTagsError, t]);

  if (!isOpen) {
    return null;
  }

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
    }
    setInputValue("");
  };

  const removeTag = (indexToRemove: number) => {
    setTags(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    } else if (e.key === "," && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.includes(",")) {
      const tagValue = value.replace(",", "");
      if (tagValue.trim()) {
        addTag(tagValue);
      }
    } else {
      setInputValue(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (inputValue.trim()) {
      addTag(inputValue);
    }

    if (tags.length === 0 && !inputValue.trim()) {
      return;
    }

    const finalTags = inputValue.trim()
      ? [...tags, inputValue.trim()]
      : [...tags];

    const tagDataArray = finalTags.map((name) => ({
      tagName: name,
    }));

    createMultipleTags({ reportId, tagDataArray });

    setTags([]);
    setInputValue("");
    onClose();
  };

  return (
    <div
      data-qa="add-tag-dialog-overlay"
      css={[
        {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1000,
        },
        cssProp,
      ]}
      onClick={onClose}
    >
      <div
        data-qa="add-tag-dialog-content"
        css={{
          backgroundColor: "#fff",
          borderRadius: 4,
          padding: 20,
          width: 400,
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 css={{ margin: "0 0 15px 0" }}>{t("Add New Tags")}</h3>
        <form data-qa="add-tag-form" onSubmit={handleSubmit}>
          <div
            data-qa="tags-input-container"
            css={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              padding: "8px 10px",
              borderRadius: 4,
              border: `1px solid ${Colors.biDashboardView.TABLE_ALT_BACKGROUND}`,
              marginBottom: 15,
              minHeight: 38,
              "&:focus-within": {
                borderColor: Colors.biDashboardView.FOCUS_COLOR,
              },
            }}
            onClick={() => inputRef.current?.focus()}
          >
            {tags.map((tag, index) => (
              <Chip
                key={`tag-${index}`}
                data-qa={`dialog-tag-chip-${index}`}
                label={tag.length > 24 ? tag.slice(0, 23) + "..." : tag}
                size="small"
                variant="filled"
                css={{ margin: "2px 4px 2px 0" }}
                onDelete={() => removeTag(index)}
              />
            ))}
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              data-qa="tag-input-field"
              placeholder={
                tags.length === 0
                  ? t("Enter tag names (separate multiple tags with commas)")
                  : ""
              }
              css={{
                flex: "1 0 auto",
                border: "none",
                outline: "none",
                fontSize: 14,
                padding: "4px 0",
                minWidth: 120,
                background: "transparent",
              }}
              autoFocus
            />
          </div>
          <div css={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              data-qa="cancel-add-tag-button"
              css={{
                padding: "8px 15px",
                borderRadius: 4,
                border: "none",
                backgroundColor: "#f0f0f0",
                cursor: "pointer",
                fontSize: 14,
                "&:hover": {
                  backgroundColor: "#e0e0e0",
                },
              }}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              disabled={
                isCreatingTag || (tags.length === 0 && !inputValue.trim())
              }
              data-qa="submit-add-tag-button"
              css={{
                padding: "8px 15px",
                borderRadius: 4,
                border: "none",
                backgroundColor: Colors.biDashboardView.FOCUS_COLOR,
                color: "#fff",
                cursor: "pointer",
                fontSize: 14,
                "&:hover": {
                  backgroundColor: Colors.biDashboardView.FOCUS_COLOR,
                },
                "&:disabled": {
                  backgroundColor: "#ccc",
                  cursor: "not-allowed",
                },
              }}
            >
              {isCreatingTag ? t("Adding...") : t("Add Tags")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTagDialog;
